import { LayoutFullWidth } from '@product-framework/components/Layout';
import { useTheme } from '@mui/material';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import FormLoading from '@ui-framework/components/AdvancedForms/FormLoading';
import { useRef, useState } from 'react';
import ServiceAreaMap from '../../components/ServiceDirectoryMap';
import MapLib from '../../components/ServiceDirectoryMap/lib/MapLib';
import ServiceBusinessSearch from '../../components/ServiceBusinessSearch';
import { fetchTechnicians } from './lib';
import TechnicianDetailsDrawer from '../../components/TechnicianDetails/Drawer';
import TechnicianDetails from '../../components/TechnicianDetails/index';

const MAP_CIRCLE_NAME = 'SP_TRAVEL_DISTANCE_CIRCLE';
const MAP_TECHNICIAN_LAYER_NAME = 'MAP_TECHNICIAN_LAYER';
const MAP_TECHNICIAN_SOURCE_NAME = 'MAP_TECHNICIAN_SOURCE_NAME';

function ServiceDirectory() {
  const theme = useTheme();
  const provider =
    process.env.NODE_ENV === 'development' ? 'serviceDirectory' : 'default';
  const { getApiUrl, get, put, post } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const mapRef = useRef(null);
  const drawerRef = useRef(null);
  const mapboxRef = useRef(null);
  const { formatMessage } = useLocalization();

  const defaults = {
    zoom: 10,
    lat: 40.56378846993402,
    lng: -99.9599284137568,
  };

  const commonParams = {
    put,
    get,
    getApiUrl,
    post,
    snackbar,
    formatMessage,
  };

  // eslint-disable-next-line no-unused-vars
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTechnician, setCurrentTechnician] = useState(null);

  const drawTravelDistanceCircle = ({ coordinates, radiusMiles }) => {
    mapRef.current.coreCreateCircle({
      coordinates,
      radiusMiles,
      points: 64,
      name: MAP_CIRCLE_NAME,
    });
  };

  const resetMapView = () => {
    mapRef.current.coreRemoveCircle(MAP_CIRCLE_NAME);
    mapRef.current.removeLayersAndSource({
      layerName: MAP_TECHNICIAN_LAYER_NAME,
      sourceName: MAP_TECHNICIAN_SOURCE_NAME,
    });
  };

  const onSubmit = async data => {
    try {
      resetMapView();
      setLoading(true);
      const response = await fetchTechnicians(commonParams, data);
      const technicianData = response?.data || [];

      if (Array.isArray(technicianData) && technicianData?.length > 0) {
        const technicianGeoJson =
          MapLib.technicianPointsToGeoJSON(technicianData);

        const radiusInMiles = MapLib.coreMetersToMiles(response?.radius);

        const sourceCenter = {
          locationLng: response?.source_location?.longitude,
          locationLat: response?.source_location?.latitude,
        };

        drawTravelDistanceCircle({
          coordinates: {
            locationLng: sourceCenter?.locationLng,
            locationLat: sourceCenter?.locationLat,
          },
          radiusMiles: radiusInMiles,
        });

        mapRef.current.addTechnicianLayer({
          layerName: MAP_TECHNICIAN_LAYER_NAME,
          sourceName: MAP_TECHNICIAN_SOURCE_NAME,
          geoJson: technicianGeoJson,
          onClick: e => {
            const technicanData = e?.features?.[0]?.properties;
            if (technicanData) {
              drawerRef?.current?.close();
              setCurrentTechnician(technicanData);
              drawerRef?.current?.open();
            }
          },
        });

        // mapRef.current.jumpTo(
        //   sourceCenter.locationLat,
        //   sourceCenter?.locationLng,
        // );
        mapRef.current.fitMapToCircle({
          center: [sourceCenter?.locationLng, sourceCenter.locationLat],
          radiusMiles: radiusInMiles,
        });
      } else {
        snackbar.showMessage({
          type: 'error',
          data: (
            <SnackbarApiErrorMessage
              errors={{ message: 'No technicians found' }}
            />
          ),
        });
      }
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={{ message: err?.message }} />,
      });
    }
    setLoading(false);
  };

  const initialize = async (mapObj, mapboxObj) => {
    mapboxRef.current = mapboxObj;

    mapRef.current = new MapLib({
      map: mapObj,
      zoom: defaults.zoom,
      speed: defaults.speed,
      theme,
      marker: {
        job: null,
        serviceProviders: [],
        selectedServiceProvider: null,
      },
    });

    await mapRef.current.coreLoadMarkers();

    setLoading(false);
  };

  const onMapReady = async (mapObj, mapboxDrawObj) => {
    setMap(mapObj);
    initialize(mapObj, mapboxDrawObj);
  };

  return (
    <LayoutFullWidth>
      <ServiceAreaMap onReady={onMapReady} {...defaults} />
      {loading && (
        <FormLoading
          circular
          sx={{
            position: 'absolute',
            top: '150px',
            width: '100%',
          }}>
          Loading Map details ...
        </FormLoading>
      )}

      <ServiceBusinessSearch
        onSubmit={data => onSubmit(data)}
        onReset={() => resetMapView()}
        containerProps={{
          sx: { position: 'absolute', left: '1%', top: 69, width: '98%' },
        }}
      />

      <TechnicianDetailsDrawer ref={drawerRef}>
        <TechnicianDetails technician={currentTechnician} />
      </TechnicianDetailsDrawer>
    </LayoutFullWidth>
  );
}

export default observer(ServiceDirectory);
